import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import FileAttachments from '../components/FileAttachments';
import { PackageCheck, Plus, XCircle, CheckCircle2, ChevronDown, ChevronUp, Paperclip, Send, Pencil, Trash2, AlertCircle } from 'lucide-react';
import { BadgeEstadoFirma, ModalPasarPdteFirma, ModalRevertirBorrador, esEstadoFirmado, IconoBloqueado } from '../components/FlujoFirmaAcciones';
import { ModalEliminarRegistro, esAdmin } from '../components/RegistroAcciones';

// ═══════════════════════════════════════════════════════════════
// SELECTOR Conforme / No Conforme con botones grandes
// ═══════════════════════════════════════════════════════════════
function CheckConforme({ label, value, onChange, disabled }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 6, color: 'var(--color-text-1)' }}>{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" disabled={disabled}
          onClick={() => onChange(value === 'conforme' ? null : 'conforme')}
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
            borderColor: value === 'conforme' ? '#10b981' : 'var(--color-border)',
            background: value === 'conforme' ? '#d1fae5' : 'white',
            color: value === 'conforme' ? '#065f46' : 'var(--color-text-2)',
            fontWeight: value === 'conforme' ? 600 : 400,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
          }}>
          <CheckCircle2 size={14} /> Conforme
        </button>
        <button type="button" disabled={disabled}
          onClick={() => onChange(value === 'no_conforme' ? null : 'no_conforme')}
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 8, border: '2px solid',
            borderColor: value === 'no_conforme' ? '#ef4444' : 'var(--color-border)',
            background: value === 'no_conforme' ? '#fee2e2' : 'white',
            color: value === 'no_conforme' ? '#991b1b' : 'var(--color-text-2)',
            fontWeight: value === 'no_conforme' ? 600 : 400,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
          }}>
          <XCircle size={14} /> No Conforme
        </button>
      </div>
    </div>
  );
}

// Selector Sí / No genérico
function CheckSiNo({ label, value, onChange, disabled }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 6, color: 'var(--color-text-1)' }}>{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" disabled={disabled}
          onClick={() => onChange(value === 'si' ? null : 'si')}
          style={{
            flex: 1, padding: '6px 10px', borderRadius: 6, border: '2px solid',
            borderColor: value === 'si' ? '#10b981' : 'var(--color-border)',
            background: value === 'si' ? '#d1fae5' : 'white',
            color: value === 'si' ? '#065f46' : 'var(--color-text-2)',
            fontWeight: value === 'si' ? 600 : 400,
            cursor: disabled ? 'not-allowed' : 'pointer'
          }}>Sí</button>
        <button type="button" disabled={disabled}
          onClick={() => onChange(value === 'no' ? null : 'no')}
          style={{
            flex: 1, padding: '6px 10px', borderRadius: 6, border: '2px solid',
            borderColor: value === 'no' ? '#ef4444' : 'var(--color-border)',
            background: value === 'no' ? '#fee2e2' : 'white',
            color: value === 'no' ? '#991b1b' : 'var(--color-text-2)',
            fontWeight: value === 'no' ? 600 : 400,
            cursor: disabled ? 'not-allowed' : 'pointer'
          }}>No</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FORMULARIO compartido (crear + editar)
// ═══════════════════════════════════════════════════════════════
function FormularioRecepcion({ form, set, disabled, proveedores, usuarios }) {
  const tieneNoConforme = ['integridad', 'concordancia', 'documentacion', 'textos', 'iu_etiquetado', 'iu_prospecto']
    .some(k => form[k] === 'no_conforme');

  return (
    <>
      {/* Datos identificativos */}
      <h4 style={{ marginBottom: 10, color: 'var(--color-primary)' }}>Datos del producto</h4>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Fecha *</label>
          <input type="date" className="form-control" value={form.fecha} onChange={e => set('fecha', e.target.value)} disabled={disabled} />
        </div>
        <div className="form-group">
          <label className="form-label">Producto / Modelo *</label>
          <input className="form-control" value={form.producto} onChange={e => set('producto', e.target.value)} disabled={disabled} />
        </div>
      </div>
      <div className="form-grid-3">
        <div className="form-group">
          <label className="form-label">Cantidad</label>
          <input className="form-control" value={form.cantidad} onChange={e => set('cantidad', e.target.value)} disabled={disabled} />
        </div>
        <div className="form-group">
          <label className="form-label">Lote</label>
          <input className="form-control" value={form.lote} onChange={e => set('lote', e.target.value)} disabled={disabled} />
        </div>
        <div className="form-group">
          <label className="form-label">Caducidad</label>
          <input type="date" className="form-control" value={form.caducidad} onChange={e => set('caducidad', e.target.value)} disabled={disabled} />
        </div>
      </div>

      <h4 style={{ marginTop: 16, marginBottom: 10, color: 'var(--color-primary)' }}>Datos del envío</h4>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Proveedor</label>
          {proveedores && proveedores.length > 0 ? (
            <input list="lista-proveedores" className="form-control" value={form.proveedor} onChange={e => set('proveedor', e.target.value)} disabled={disabled} />
          ) : (
            <input className="form-control" value={form.proveedor} onChange={e => set('proveedor', e.target.value)} disabled={disabled} />
          )}
          <datalist id="lista-proveedores">
            {proveedores?.map(p => <option key={p.id} value={p.nombre} />)}
          </datalist>
        </div>
        <div className="form-group">
          <label className="form-label">Nº Albarán</label>
          <input className="form-control" value={form.num_albaran} onChange={e => set('num_albaran', e.target.value)} disabled={disabled} />
        </div>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Fecha Albarán</label>
          <input type="date" className="form-control" value={form.fecha_albaran} onChange={e => set('fecha_albaran', e.target.value)} disabled={disabled} />
        </div>
        <div className="form-group">
          <label className="form-label">Recepcionado por</label>
          <input list="lista-usuarios" className="form-control" value={form.recepcionado_por} onChange={e => set('recepcionado_por', e.target.value)} disabled={disabled} />
          <datalist id="lista-usuarios">
            {usuarios?.map(u => <option key={u.id} value={`${u.nombre} ${u.apellidos || ''}`.trim()} />)}
          </datalist>
        </div>
      </div>

      {/* Verificaciones principales */}
      <h4 style={{ marginTop: 16, marginBottom: 10, color: 'var(--color-primary)' }}>Verificaciones</h4>
      <CheckConforme label="INTEGRIDAD: el bulto no presenta zonas dañadas externamente y el material recibido se encuentra intacto"
        value={form.integridad} onChange={v => set('integridad', v)} disabled={disabled} />
      <CheckConforme label="CONCORDANCIA: existe concordancia entre el albarán, el pedido y el material recibido"
        value={form.concordancia} onChange={v => set('concordancia', v)} disabled={disabled} />
      <CheckConforme label="DOCUMENTACIÓN: el bulto ha llegado con la documentación de entrega necesaria"
        value={form.documentacion} onChange={v => set('documentacion', v)} disabled={disabled} />

      {/* Etiquetado */}
      <h4 style={{ marginTop: 16, marginBottom: 10, color: 'var(--color-primary)' }}>Etiquetado</h4>
      <div className="form-grid">
        <CheckSiNo label="Marcado CE" value={form.marcado_ce} onChange={v => set('marcado_ce', v)} disabled={disabled} />
        {form.marcado_ce === 'si' && (
          <div className="form-group">
            <label className="form-label">O.N. nº (Organismo Notificado)</label>
            <input className="form-control" value={form.marcado_ce_on} onChange={e => set('marcado_ce_on', e.target.value)} disabled={disabled} placeholder="ej. 0123" />
          </div>
        )}
      </div>
      <CheckConforme label="Textos" value={form.textos} onChange={v => set('textos', v)} disabled={disabled} />
      <div className="form-grid-3">
        <CheckSiNo label="Datos Fabricante" value={form.datos_fabricante} onChange={v => set('datos_fabricante', v)} disabled={disabled} />
        <CheckSiNo label="Datos Proveedor/Distribuidor" value={form.datos_proveedor} onChange={v => set('datos_proveedor', v)} disabled={disabled} />
        <CheckSiNo label="Datos Rep. UE (si aplica)" value={form.datos_rep_ue} onChange={v => set('datos_rep_ue', v)} disabled={disabled} />
      </div>

      {/* Instrucciones de uso */}
      <h4 style={{ marginTop: 16, marginBottom: 10, color: 'var(--color-primary)' }}>Instrucciones de uso</h4>
      <CheckConforme label="En etiquetado" value={form.iu_etiquetado} onChange={v => set('iu_etiquetado', v)} disabled={disabled} />
      <CheckConforme label="En prospecto / documentación" value={form.iu_prospecto} onChange={v => set('iu_prospecto', v)} disabled={disabled} />

      <div className="form-group" style={{ marginTop: 16 }}>
        <label className="form-label">Observaciones</label>
        <textarea className="form-control" rows={3} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} disabled={disabled} />
      </div>

      {tieneNoConforme && (
        <div className="alert alert-warning" style={{ marginTop: 10, fontSize: '0.85rem' }}>
          <AlertCircle size={16} /> Has marcado al menos un campo como <strong>NO CONFORME</strong>. Recuerda cumplimentar también la <strong>Hoja de Incidencias</strong>.
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Crear nueva recepción
// ═══════════════════════════════════════════════════════════════
function ModalRecepcion({ onClose, onGuardado, proveedores, usuarios }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    fecha: today, producto: '', cantidad: '', lote: '', caducidad: '',
    proveedor: '', num_albaran: '', fecha_albaran: '', recepcionado_por: '',
    integridad: null, concordancia: null, documentacion: null,
    marcado_ce: null, marcado_ce_on: '', textos: null,
    datos_fabricante: null, datos_proveedor: null, datos_rep_ue: null,
    iu_etiquetado: null, iu_prospecto: null,
    observaciones: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.producto) { setError('El producto es obligatorio'); return; }
    setLoading(true);
    try { const r = await api.post('/registros/recepciones', form); setSavedId(r.data.id); }
    catch (e) { setError(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PackageCheck size={18} color="var(--color-primary)" />Nueva Recepción de Mercancías
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {savedId && <div className="alert alert-success"><CheckCircle2 size={16} /> Recepción guardada en estado Borrador. Puedes adjuntar el albarán.</div>}

          <FormularioRecepcion form={form} set={set} disabled={!!savedId} proveedores={proveedores} usuarios={usuarios} />

          <FileAttachments seccion="recepciones" entidadTipo="recepcion" entidadId={savedId} label="Adjuntar albarán / fotografía / documentación" />
        </div>
        <div className="modal-footer">
          {!savedId ? (
            <><button className="btn btn-outline" onClick={onClose}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardar} disabled={loading}>
                {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />} Guardar
              </button></>
          ) : (
            <><button className="btn btn-outline" onClick={onClose}>Cerrar sin más archivos</button>
              <button className="btn btn-primary" onClick={onGuardado}><CheckCircle2 size={16} /> Finalizar</button></>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Editar recepción (admin + contraseña)
// ═══════════════════════════════════════════════════════════════
function ModalEditarRecepcion({ registro, onClose, onActualizado, proveedores, usuarios }) {
  const [form, setForm] = useState({
    fecha: registro.fecha, producto: registro.producto || '',
    cantidad: registro.cantidad || '', lote: registro.lote || '', caducidad: registro.caducidad || '',
    proveedor: registro.proveedor || '', num_albaran: registro.num_albaran || '',
    fecha_albaran: registro.fecha_albaran || '', recepcionado_por: registro.recepcionado_por || '',
    integridad: registro.integridad, concordancia: registro.concordancia, documentacion: registro.documentacion,
    marcado_ce: registro.marcado_ce, marcado_ce_on: registro.marcado_ce_on || '', textos: registro.textos,
    datos_fabricante: registro.datos_fabricante, datos_proveedor: registro.datos_proveedor, datos_rep_ue: registro.datos_rep_ue,
    iu_etiquetado: registro.iu_etiquetado, iu_prospecto: registro.iu_prospecto,
    observaciones: registro.observaciones || ''
  });
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGuardar = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      await api.put(`/registros/recepciones/${registro.id}`, { ...form, password });
      onActualizado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={20} color="#3b82f6" /> Editar Recepción {registro.numero_recepcion}
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
            <AlertCircle size={16} /> <strong>Acción restringida:</strong> Solo administradores. Se requiere contraseña.
          </div>

          <FormularioRecepcion form={form} set={set} disabled={false} proveedores={proveedores} usuarios={usuarios} />

          <div className="form-group" style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
            <label className="form-label" style={{ color: '#92400e', fontWeight: 600 }}>🔐 Confirma con tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Tu contraseña actual" />
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGuardar} disabled={loading || !password}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FILA con todos los botones de acción
// ═══════════════════════════════════════════════════════════════
function FilaRecepcion({ r, isAdmin, onPasarPdte, onRevertir, onEditar, onEliminar }) {
  const [expanded, setExpanded] = useState(false);
  const estado = r.estado || 'borrador';
  const bloqueado = esEstadoFirmado(estado, false);

  // Hay alguna NO conformidad?
  const hayNoConforme = ['integridad', 'concordancia', 'documentacion', 'textos', 'iu_etiquetado', 'iu_prospecto']
    .some(k => r[k] === 'no_conforme');

  return (
    <>
      <tr>
        <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--color-primary)', fontWeight: 700 }}>{r.numero_recepcion}</span></td>
        <td>{new Date(r.fecha).toLocaleDateString('es-ES')}</td>
        <td style={{ fontWeight: 500 }}>{r.producto}</td>
        <td style={{ fontSize: '0.82rem' }}>{r.proveedor || '—'}</td>
        <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>{r.num_albaran || '—'}</td>
        <td>
          {hayNoConforme ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#dc2626', fontWeight: 600, fontSize: '0.8rem' }}>
              <AlertCircle size={13} /> No Conforme
            </span>
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#16a34a', fontSize: '0.8rem' }}>
              <CheckCircle2 size={13} /> Conforme
            </span>
          )}
        </td>
        <td><BadgeEstadoFirma estado={estado} /></td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FileAttachments seccion="recepciones" entidadTipo="recepcion" entidadId={r.id} compact />
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {estado === 'borrador' && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onPasarPdte(r)}
                title="Pasar a Pdte. firma" style={{ color: '#f59e0b' }}>
                <Send size={13} />
              </button>
            )}

            {estado === 'pendiente_firma' && isAdmin && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onRevertir(r)}
                title="Revertir a Borrador (admin)" style={{ color: '#6b7280' }}>
                <ChevronUp size={13} style={{ transform: 'rotate(-90deg)' }} />
              </button>
            )}

            {isAdmin && !bloqueado && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEditar(r)}
                title="Editar (admin)" style={{ color: '#3b82f6' }}>
                <Pencil size={13} />
              </button>
            )}

            {isAdmin && !bloqueado && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEliminar(r)}
                title="Eliminar (admin)" style={{ color: 'var(--color-danger)' }}>
                <Trash2 size={13} />
              </button>
            )}

            {bloqueado && <IconoBloqueado estado={estado} />}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr><td colSpan={8} style={{ padding: '0 14px 14px', background: 'var(--color-gray-1)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: '12px 0' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Cantidad / Lote / Caducidad</div>
              <div style={{ fontWeight: 500 }}>{r.cantidad || '—'} · {r.lote || '—'} · {r.caducidad ? new Date(r.caducidad).toLocaleDateString('es-ES') : '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Recepcionado por</div>
              <div style={{ fontWeight: 500 }}>{r.recepcionado_por || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Verificaciones</div>
              <div style={{ fontSize: '0.85rem' }}>
                Integridad: <b>{r.integridad || '—'}</b> · Concordancia: <b>{r.concordancia || '—'}</b> · Documentación: <b>{r.documentacion || '—'}</b>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Etiquetado</div>
              <div style={{ fontSize: '0.85rem' }}>
                CE: <b>{r.marcado_ce || '—'}{r.marcado_ce_on ? ` (${r.marcado_ce_on})` : ''}</b> · Textos: <b>{r.textos || '—'}</b>
              </div>
            </div>
            {r.observaciones && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-2)', marginBottom: 2 }}>Observaciones</div>
                <div style={{ fontSize: '0.85rem' }}>{r.observaciones}</div>
              </div>
            )}
          </div>
          <FileAttachments seccion="recepciones" entidadTipo="recepcion" entidadId={r.id} label="Subir albarán o documentación" />
        </td></tr>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// PÁGINA principal
// ═══════════════════════════════════════════════════════════════
export default function RecepcionesPage() {
  const [registros, setRegistros] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editarRegistro, setEditarRegistro] = useState(null);
  const [eliminarRegistro, setEliminarRegistro] = useState(null);
  const [pasarPdteRegistro, setPasarPdteRegistro] = useState(null);
  const [revertirRegistro, setRevertirRegistro] = useState(null);
  const [success, setSuccess] = useState('');
  const isAdmin = esAdmin();

  const load = () => { setLoading(true); api.get('/registros/recepciones').then(r => setRegistros(r.data)).finally(() => setLoading(false)); };
  const loadProveedores = () => { api.get('/proveedores').then(r => setProveedores(r.data)).catch(() => {}); };
  const loadUsuarios = () => { api.get('/usuarios').then(r => setUsuarios(r.data)).catch(() => {}); };

  useEffect(() => { load(); loadProveedores(); loadUsuarios(); }, []);

  const mostrarSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PackageCheck size={24} color="var(--color-primary)" />Recepción de Mercancías
          </h1>
          <p>{registros.length} recepciones · Cada recepción se firma individualmente por la Directora Técnica</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={16} /> Nueva Recepción</button>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}><CheckCircle2 size={16} /> {success}</div>}

      <div className="card">
        {loading ? <div className="loading-overlay"><div className="spinner" /></div> :
          registros.length === 0 ? <div className="empty-state"><PackageCheck size={48} /><h3>Sin recepciones registradas</h3></div> : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>Nº</th><th>Fecha</th><th>Producto</th><th>Proveedor</th>
                  <th>Albarán</th><th>Verificación</th><th>Estado</th><th><Paperclip size={13} /></th>
                </tr></thead>
                <tbody>{registros.map(r =>
                  <FilaRecepcion key={r.id} r={r} isAdmin={isAdmin}
                    onPasarPdte={setPasarPdteRegistro}
                    onRevertir={setRevertirRegistro}
                    onEditar={setEditarRegistro}
                    onEliminar={setEliminarRegistro} />)}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {modal && <ModalRecepcion onClose={() => setModal(false)}
        onGuardado={() => { setModal(false); load(); }}
        proveedores={proveedores} usuarios={usuarios} />}

      {pasarPdteRegistro && <ModalPasarPdteFirma
        endpoint={`/registros/recepciones/${pasarPdteRegistro.id}/pasar-pdte-firma`}
        descripcion={`${pasarPdteRegistro.numero_recepcion} · ${pasarPdteRegistro.producto}`}
        onClose={() => setPasarPdteRegistro(null)}
        onConfirmado={() => { setPasarPdteRegistro(null); load(); mostrarSuccess('Marcado como pendiente de firma. Ve a Firmas Pendientes.'); }} />}

      {revertirRegistro && <ModalRevertirBorrador
        endpoint={`/registros/recepciones/${revertirRegistro.id}/reset-estado`}
        descripcion={`${revertirRegistro.numero_recepcion} · ${revertirRegistro.producto}`}
        onClose={() => setRevertirRegistro(null)}
        onConfirmado={() => { setRevertirRegistro(null); load(); mostrarSuccess('Revertido a borrador'); }} />}

      {editarRegistro && <ModalEditarRecepcion registro={editarRegistro}
        proveedores={proveedores} usuarios={usuarios}
        onClose={() => setEditarRegistro(null)}
        onActualizado={() => { setEditarRegistro(null); load(); mostrarSuccess('Recepción actualizada'); }} />}

      {eliminarRegistro && <ModalEliminarRegistro
        endpoint={`/registros/recepciones/${eliminarRegistro.id}`}
        descripcion={`${eliminarRegistro.numero_recepcion} · ${eliminarRegistro.producto}`}
        onClose={() => setEliminarRegistro(null)}
        onEliminado={() => { setEliminarRegistro(null); load(); mostrarSuccess('Recepción eliminada'); }} />}
    </div>
  );
}
